// leadSimulatorV2Logic.ts — PURE logic for the v2 Lead Simulator drawer.
//
// Everything in here is React-free and network-free so it can be unit-tested by
// the throwaway vitest recipe (frontend/node_modules is a partial install — a
// .tsx component cannot be rendered locally, so all decision logic lives here
// and the drawer is thin wiring over it).
//
// Only `import type` from '@/api/leadRouter' — type imports are erased at
// transform time, so this module has ZERO runtime imports and always collects.

import type {
  CategoryResolutionRule,
  GeoPincode,
  SimulateInput,
  SimulateResult,
  SimulateV2BatchLead,
  SimulateV2Input,
  SimulateV2Result,
  SimulateV2Candidate,
} from '@/api/leadRouter';

// ─── Form state ───────────────────────────────────────────────────────────────

export type InterestChoice = '' | 'HOT' | 'WARM' | 'COLD';
export type DestinationChoice = '' | 'user' | 'dealer_dispatch' | 'triage';

export interface SimV2FormState {
  capturedCategory: string;
  applicationType: string;
  productGroupOverride: string;
  city: string;
  district: string;
  state: string;
  pincode: string;
  /** true once a pincode resolved against PincodeArea — district/state are then engine-derived. */
  pincodeLocked: boolean;
  pincodeConfident: boolean;
  interestLevel: InterestChoice;
  /** 3-way toggle kept from v1; null = "not set" which the engine reads as false. */
  b2b: boolean | null;
  humanTouched: boolean;
  expectedOwnerUserId: string;
  expectedDealerId: string;
  expectedDestination: DestinationChoice;
}

export function emptySimV2Form(): SimV2FormState {
  return {
    capturedCategory: '',
    applicationType: '',
    productGroupOverride: '',
    city: '',
    district: '',
    state: '',
    pincode: '',
    pincodeLocked: false,
    pincodeConfident: false,
    interestLevel: '',
    b2b: null,
    humanTouched: false,
    expectedOwnerUserId: '',
    expectedDealerId: '',
    expectedDestination: '',
  };
}

// ─── Option derivation — DB-driven, never hardcoded ───────────────────────────

/**
 * Distinct product categories, straight off the tenant's CategoryResolutionRule
 * rows. There is deliberately no hardcoded list: the resolver is DB-driven and a
 * hardcoded array would silently desync the moment someone adds a rule.
 */
export function deriveCategoryOptions(rules: CategoryResolutionRule[]): string[] {
  const seen = new Set<string>();
  for (const r of rules) {
    const v = (r.capturedCategory ?? '').trim();
    if (v) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/**
 * Is this category application-typed at all? Only categories that have at least
 * one rule carrying a non-null applicationType are — for every other category
 * the engine ignores applicationType entirely, so the control must be disabled
 * rather than accepting input that does nothing.
 *
 * NOTE: derived from the rules, never from a hardcoded "Heat Pump" string.
 */
export function isCategoryApplicationTyped(
  rules: CategoryResolutionRule[],
  category: string,
): boolean {
  const c = category.trim();
  if (!c) return false;
  return rules.some(
    (r) => r.capturedCategory === c && (r.applicationType ?? '').trim() !== '',
  );
}

/** Distinct non-null applicationTypes for the chosen category, sorted. */
export function deriveApplicationTypeOptions(
  rules: CategoryResolutionRule[],
  category: string,
): string[] {
  const c = category.trim();
  if (!c) return [];
  const seen = new Set<string>();
  for (const r of rules) {
    if (r.capturedCategory !== c) continue;
    const v = (r.applicationType ?? '').trim();
    if (v) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

export const APP_TYPE_DISABLED_NOTE =
  'This category is not application-typed — the engine ignores this field.';

export const CITY_NOTE =
  'Pincode data is district-grained — there is no city list. The engine still matches city at rank 10, ' +
  'so type it exactly as your RoutingAssignment city rows spell it.';

export const PINCODE_CONFIDENT_NOTE = 'Off means the rank-5 pincode tier is skipped.';

export const HUMAN_TOUCHED_NOTE =
  'On makes a HOT/B2B escalation notify-only instead of moving the owner.';

// ─── Dependent-control transitions ────────────────────────────────────────────

/**
 * Changing the category must drop an applicationType that is no longer valid,
 * otherwise the payload carries a stale app type from the previous category.
 */
export function applyCategoryChange(
  form: SimV2FormState,
  rules: CategoryResolutionRule[],
  nextCategory: string,
): SimV2FormState {
  const valid = deriveApplicationTypeOptions(rules, nextCategory);
  const keep = form.applicationType && valid.includes(form.applicationType);
  return { ...form, capturedCategory: nextCategory, applicationType: keep ? form.applicationType : '' };
}

export function isValidPincode(pincode: string): boolean {
  return /^\d{6}$/.test(pincode.trim());
}

/**
 * A pincode that resolved against PincodeArea fills BOTH district and state and
 * locks them — that mirrors the geocode-at-import path where the pincode row
 * wins over whatever the lead carried.
 */
export function applyPincodeResolved(form: SimV2FormState, geo: GeoPincode): SimV2FormState {
  return {
    ...form,
    pincode: geo.pincode,
    district: geo.district,
    state: geo.state,
    pincodeLocked: true,
  };
}

/** Pincode cleared or unresolvable → district/state become editable again. */
export function applyPincodeCleared(form: SimV2FormState): SimV2FormState {
  return { ...form, pincode: '', pincodeLocked: false };
}

/** Changing state invalidates a district picked under the previous state. */
export function applyStateChange(form: SimV2FormState, nextState: string): SimV2FormState {
  if (form.state === nextState) return form;
  return { ...form, state: nextState, district: '' };
}

// ─── Payload construction ─────────────────────────────────────────────────────

function nn(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

export function buildExpected(form: SimV2FormState): SimulateV2Input['expected'] {
  const ownerUserId = nn(form.expectedOwnerUserId);
  const dealerId = nn(form.expectedDealerId);
  const destination = form.expectedDestination === '' ? null : form.expectedDestination;
  if (ownerUserId === null && dealerId === null && destination === null) return null;
  return { ownerUserId, dealerId, destination };
}

export function hasExpectation(form: SimV2FormState): boolean {
  return buildExpected(form) !== null;
}

/**
 * The v2 request body. The fields that make this v2 and not v1 — `district`,
 * `pincode`, `pincodeConfident`, `humanTouched` — have no slot in v1's
 * SimulateInput at all, which is exactly what makes them worth asserting on.
 */
export function buildSimulateV2Payload(form: SimV2FormState): SimulateV2Input {
  const body: SimulateV2Input = {
    capturedCategory: nn(form.capturedCategory),
    applicationType: nn(form.applicationType),
    city: nn(form.city),
    district: nn(form.district),
    state: nn(form.state),
    pincode: isValidPincode(form.pincode) ? form.pincode.trim() : null,
    pincodeConfident: form.pincodeConfident,
    interestLevel: form.interestLevel === '' ? null : form.interestLevel,
    isB2B: form.b2b === true,
    humanTouched: form.humanTouched,
  };
  const override = nn(form.productGroupOverride);
  if (override) body.productGroupOverride = override;
  const expected = buildExpected(form);
  if (expected) body.expected = expected;
  return body;
}

// ─── Result reading ───────────────────────────────────────────────────────────

export function chosenCandidate(result: SimulateV2Result): SimulateV2Candidate | null {
  return result.trace?.candidates?.find((c) => c.chosen) ?? null;
}

export function rejectedCandidates(result: SimulateV2Result): SimulateV2Candidate[] {
  return (result.trace?.candidates ?? []).filter((c) => !c.chosen);
}

export interface AssertionView {
  present: boolean;
  pass: boolean;
  label: string;
  mismatches: string[];
}

/** Formats the backend assertion block for the drawer's pass/fail banner. */
export function describeAssertion(result: SimulateV2Result): AssertionView {
  const a = result.assertion;
  if (!a) return { present: false, pass: false, label: 'No expectation set', mismatches: [] };
  return {
    present: true,
    pass: a.pass,
    label: a.pass ? 'PASS — routed as expected' : 'FAIL — routed somewhere else',
    mismatches: a.mismatches ?? [],
  };
}

export function destinationLabel(d: SimulateV2Result['destination']): string {
  if (d === 'user') return 'Assigned to person';
  if (d === 'dealer_dispatch') return 'Dispatched to dealer';
  return 'Triage';
}

/** Winner name for the result card: owner → dealer → triage reason. */
export function winnerLabel(result: SimulateV2Result): string {
  if (result.destination === 'user') {
    return result.ownerName ?? result.ownerUserId ?? 'Unknown user';
  }
  if (result.destination === 'dealer_dispatch') {
    return result.dealerName ?? result.assignedDealerId ?? 'Unknown dealer';
  }
  return result.triageReason ?? 'Triage';
}

// ─── Legacy bridge ────────────────────────────────────────────────────────────

/**
 * The Advanced page (LeadRouterPage) highlights its routing graph off the v1
 * SimulateResult shape via the pre-existing `window.__lrSimCb` hack. v2 never
 * runs the overlay table, so `decidedBy` is always '4-stage' and overlayRuleId
 * is always null — that is honest, not a stub: the overlay path genuinely does
 * not participate in a v2 decision.
 */
export function toLegacyHighlight(
  payload: SimulateV2Input,
  result: SimulateV2Result,
): { lead: SimulateInput; result: SimulateResult } {
  const r = result.resolvedLead;
  return {
    lead: {
      capturedCategory: r?.capturedCategory ?? payload.capturedCategory ?? null,
      applicationType: r?.applicationType ?? payload.applicationType ?? null,
      location: r?.city ?? payload.city ?? null,
      locationState: r?.state ?? payload.state ?? null,
      interestLevel: r?.interestLevel ?? payload.interestLevel ?? null,
      isB2bPriority: r?.isB2B ?? payload.isB2B ?? false,
    },
    result: {
      assignee: result.ownerUserId
        ? { userId: result.ownerUserId, name: result.ownerName ?? null }
        : null,
      dealerId: result.assignedDealerId,
      matchedStage: result.trace?.group ?? null,
      action: result.destination,
      temperature: result.trace?.tier ?? null,
      trace: result.trace?.lines ?? [],
      decidedBy: '4-stage',
      overlayRuleId: null,
    },
  };
}

// ─── Batch mode ───────────────────────────────────────────────────────────────

export type BatchVaryBy = 'state' | 'district' | 'pincode';

export const BATCH_LIMIT = 200;

/**
 * Expands one base form into a grid of leads by varying a single geo axis.
 * Keeps the batch builder dropdown-driven (multi-select of real geo values)
 * instead of reintroducing a free-text CSV box.
 *
 * Varying by state clears district+pincode (a district from another state would
 * never match); varying by district keeps the base state; varying by pincode
 * clears district+state so the pincode row is what the backend derives from.
 */
export function expandBatchLeads(
  form: SimV2FormState,
  varyBy: BatchVaryBy,
  values: string[],
): SimulateV2BatchLead[] {
  const out: SimulateV2BatchLead[] = [];
  for (const raw of values) {
    const v = raw.trim();
    if (!v) continue;
    let variant: SimV2FormState;
    if (varyBy === 'state') {
      variant = { ...form, state: v, district: '', pincode: '', pincodeLocked: false };
    } else if (varyBy === 'district') {
      variant = { ...form, district: v, pincode: '', pincodeLocked: false };
    } else {
      variant = { ...form, pincode: v, district: '', state: '', pincodeLocked: false };
    }
    out.push({ ...buildSimulateV2Payload(variant), label: v });
    if (out.length >= BATCH_LIMIT) break;
  }
  return out;
}

export interface BatchGuard {
  ok: boolean;
  count: number;
  message: string | null;
}

export function checkBatchSize(leads: unknown[]): BatchGuard {
  const count = leads.length;
  if (count === 0) return { ok: false, count, message: 'Pick at least one value to vary by.' };
  if (count > BATCH_LIMIT) {
    return { ok: false, count, message: `Batch limit is ${BATCH_LIMIT} leads — ${count} selected.` };
  }
  return { ok: true, count, message: null };
}
